import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowDown, ArrowLeft, ArrowUp, Copy, FileUp, History, ImagePlus, Loader2, Plus, RotateCcw, Save, Trash2,
} from "lucide-react";
import { focusApi } from "../../services/focusApi";
import FocusStage from "./FocusStage";
import { StatusPill } from "./FocusTechPage";

// Au-delà, le HTML (souvent une page entière avec sa géométrie) ne s'édite plus à la main : on remplace le fichier.
const EDITABLE_CHARS = 300000;
const STARTER = `<div style="padding:72px 88px; height:100%; display:flex; flex-direction:column; justify-content:center">
  <h1>Titre qui affirme quelque chose</h1>
  <p style="font-size:28px; color:var(--df-muted)">Une phrase pour dire pourquoi c'est intéressant pour l'équipe.</p>
</div>`;

const titleOf = (html, fallback) => {
  const match = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html || "");
  return (match ? match[1].trim() : fallback || "").slice(0, 200);
};

const sizeLabel = (chars) => (chars > 1e6 ? `${(chars / 1e6).toFixed(1).replace(".", ",")} Mo`
  : `${Math.max(1, Math.round(chars / 1000))} ko`);

const plain = (slides) => slides.map(({ id, title, html, notes }) => ({ id, title, html, notes }));

const FocusTechEditor = () => {
  const { focusId } = useParams();
  const navigate = useNavigate();
  const [focus, setFocus] = useState(null);
  const [draft, setDraft] = useState(null); // diapos en cours d'édition (sans les documents)
  const [index, setIndex] = useState(0);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assets, setAssets] = useState([]);
  const [meta, setMeta] = useState({ title: "", summary: "", circle_id: "" });
  const [circles, setCircles] = useState([]);
  const [copied, setCopied] = useState(null);
  const importRef = useRef(null);
  const replaceRef = useRef(null);
  const imageRef = useRef(null);

  const mine = Boolean(focus?.mine);
  const viewingOld = Boolean(focus?.viewing_revision);

  const accept = useCallback((data) => {
    setFocus(data);
    setDraft(plain(data.slides));
    setMeta({ title: data.title, summary: data.summary || "", circle_id: data.circle_id ? String(data.circle_id) : "" });
    setIndex((current) => Math.min(current, Math.max(0, data.slides.length - 1)));
  }, []);

  const load = useCallback(async (revision) => {
    setError(null);
    try {
      accept(await focusApi.get(focusId, revision));
    } catch (err) {
      setError(err.message);
    }
  }, [accept, focusId]);

  useEffect(() => {
    load();
    focusApi.circles().then(setCircles).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (mine) focusApi.assets(focusId).then(setAssets).catch(() => {});
  }, [mine, focusId]);

  const dirty = useMemo(() => {
    if (!focus || !draft) return false;
    return JSON.stringify(plain(focus.slides)) !== JSON.stringify(draft)
      || meta.title !== focus.title || meta.summary !== (focus.summary || "")
      || meta.circle_id !== (focus.circle_id ? String(focus.circle_id) : "");
  }, [focus, draft, meta]);

  const save = useCallback(async (slides = draft) => {
    if (!mine || viewingOld) return;
    setSaving(true);
    setError(null);
    try {
      accept(await focusApi.update(focusId, {
        title: meta.title.trim() || focus.title, summary: meta.summary,
        circle_id: meta.circle_id ? Number(meta.circle_id) : null, slides, base_revision: focus.revision,
      }));
    } catch (err) {
      setError(err.status === 409 ? `${err.message} Recharge la page (tes modifications non enregistrées seront perdues).` : err.message);
    } finally {
      setSaving(false);
    }
  }, [accept, draft, focus, focusId, meta, mine, viewingOld]);

  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        if (dirty) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dirty, save]);

  if (!focus || !draft) {
    return (
      <div className="mx-auto max-w-6xl py-10">
        {error ? <p className="text-sm text-red-600">{error}</p> : <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
      </div>
    );
  }

  const current = draft[index];
  const editSlide = (patch) => setDraft(draft.map((slide, i) => (i === index ? { ...slide, ...patch } : slide)));
  const addSlides = (slides, at = draft.length) => {
    const next = [...draft.slice(0, at), ...slides, ...draft.slice(at)];
    setDraft(next);
    setIndex(at);
    return next;
  };
  const move = (from, to) => {
    if (to < 0 || to >= draft.length) return;
    const next = [...draft];
    const [slide] = next.splice(from, 1);
    next.splice(to, 0, slide);
    setDraft(next);
    setIndex(to);
  };
  const remove = (at) => {
    if (!window.confirm(`Retirer la diapo « ${draft[at].title || at + 1} » ?`)) return;
    setDraft(draft.filter((_, i) => i !== at));
    setIndex(Math.max(0, at - 1));
  };

  const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Fichier illisible."));
    reader.readAsText(file, "utf-8");
  });

  const importPages = async (event, replace = false) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    try {
      const pages = await Promise.all(files.map(async (file) => {
        const html = await readFile(file);
        return { id: undefined, title: titleOf(html, file.name.replace(/\.html?$/i, "")), html, notes: "" };
      }));
      // Une page entière s'enregistre aussitôt : trop lourde pour rester un brouillon.
      const next = replace
        ? draft.map((slide, i) => (i === index ? { ...slide, html: pages[0].html } : slide))
        : addSlides(pages, index + (draft.length ? 1 : 0));
      await save(next);
    } catch (err) {
      setError(err.message);
    }
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const asset = await focusApi.uploadAsset(focusId, file);
      setAssets(await focusApi.assets(focusId));
      copy(asset.ref);
    } catch (err) {
      setError(err.message);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(null), 1500);
    }).catch(() => {});
  };

  const restore = async (revision) => {
    if (!window.confirm(`Revenir à la version ${revision} ? (la version actuelle reste dans l'historique)`)) return;
    try {
      accept(await focusApi.restore(focusId, revision));
    } catch (err) {
      setError(err.message);
    }
  };

  const destroy = async () => {
    if (!window.confirm("Supprimer ce Focus tech et toutes ses diapos ?")) return;
    try {
      await focusApi.remove(focusId);
      navigate("/tools/focus-tech");
    } catch (err) {
      setError(err.message);
    }
  };

  const savedSlides = focus.slides;
  const previewIndex = Math.min(index, Math.max(0, savedSlides.length - 1));
  const unsavedHere = current && JSON.stringify(plain([savedSlides[index] || {}])[0]) !== JSON.stringify(current);
  const big = (current?.html || "").length > EDITABLE_CHARS;
  const button = "inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-indigo-200 hover:text-indigo-700 disabled:opacity-50";

  return (
    <div className="mx-auto max-w-7xl space-y-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link to="/tools/focus-tech" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-indigo-600">
            <ArrowLeft size={13} /> Focus tech
          </Link>
          {mine && !viewingOld ? (
            <input
              value={meta.title}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              maxLength={200}
              className="mt-1 w-full rounded-lg border border-transparent bg-transparent px-1 text-2xl font-semibold text-slate-950 outline-none hover:border-slate-200 focus:border-indigo-200"
            />
          ) : (
            <h1 className="mt-1 text-2xl font-semibold text-slate-950">{focus.title}</h1>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <StatusPill focus={focus} />
            <span>par {focus.author_name}</span>
            {mine && !viewingOld ? (
              <select value={meta.circle_id} onChange={(e) => setMeta({ ...meta, circle_id: e.target.value })}
                      className="rounded border border-slate-200 bg-white px-1 py-0.5">
                <option value="">tous mes cercles</option>
                {circles.map((circle) => <option key={circle.id} value={circle.id}>{circle.name}</option>)}
              </select>
            ) : (
              <span>{focus.circle_name || "tous ses cercles"}</span>
            )}
            {viewingOld && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                Version {focus.viewing_revision} (ancienne)
              </span>
            )}
          </div>
        </div>
        {mine && (
          <div className="flex flex-wrap items-center gap-2">
            {focus.status === "presented" && (
              <button type="button" className={button}
                      onClick={() => focusApi.reopen(focusId).then(() => load()).catch((err) => setError(err.message))}>
                <RotateCcw size={13} /> Remettre à présenter
              </button>
            )}
            {viewingOld ? (
              <>
                <button type="button" className={button} onClick={() => load()}>Version actuelle</button>
                <button type="button" className={button} onClick={() => restore(focus.viewing_revision)}>
                  <RotateCcw size={13} /> Revenir à cette version
                </button>
              </>
            ) : (
              <button type="button" onClick={() => save()} disabled={!dirty || saving}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {dirty ? "Enregistrer (Ctrl+S)" : "Enregistré"}
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 lg:grid-cols-[15rem_1fr]">
        <aside className="space-y-3">
          <div className="space-y-1.5 rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
            {draft.length === 0 && <p className="p-2 text-xs italic text-slate-400">Pas encore de diapo.</p>}
            {draft.map((slide, i) => (
              <div key={slide.id || `new-${i}`}
                   className={`group flex items-center gap-1 rounded-lg border px-2 py-1.5 text-sm ${i === index ? "border-indigo-200 bg-indigo-50" : "border-transparent hover:bg-slate-50"}`}>
                <button type="button" onClick={() => setIndex(i)} className="min-w-0 flex-1 truncate text-left">
                  <span className="mr-1 text-xs tabular-nums text-slate-400">{i + 1}.</span>
                  {slide.title || "Sans titre"}
                </button>
                {mine && !viewingOld && (
                  <span className="hidden shrink-0 items-center group-hover:flex">
                    <button type="button" onClick={() => move(i, i - 1)} className="p-0.5 text-slate-400 hover:text-indigo-600" aria-label="Monter"><ArrowUp size={12} /></button>
                    <button type="button" onClick={() => move(i, i + 1)} className="p-0.5 text-slate-400 hover:text-indigo-600" aria-label="Descendre"><ArrowDown size={12} /></button>
                    <button type="button" onClick={() => remove(i)} className="p-0.5 text-slate-400 hover:text-red-600" aria-label="Retirer"><Trash2 size={12} /></button>
                  </span>
                )}
              </div>
            ))}
            {mine && !viewingOld && (
              <div className="flex flex-col gap-1.5 pt-1">
                <button type="button" className={button}
                        onClick={() => addSlides([{ title: "Nouvelle diapo", html: STARTER, notes: "" }], index + (draft.length ? 1 : 0))}>
                  <Plus size={13} /> Nouvelle diapo
                </button>
                <button type="button" className={button} onClick={() => importRef.current?.click()}>
                  <FileUp size={13} /> Importer une page HTML
                </button>
                <input ref={importRef} type="file" accept=".html,.htm,text/html" multiple hidden
                       onChange={(e) => importPages(e)} />
              </div>
            )}
          </div>

          {mine && !viewingOld && (
            <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Images</h3>
                <button type="button" className={button} onClick={() => imageRef.current?.click()}>
                  <ImagePlus size={13} /> Ajouter
                </button>
                <input ref={imageRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden
                       onChange={uploadImage} />
              </div>
              {assets.length === 0 ? (
                <p className="text-[11px] text-slate-400">Une image ajoutée s'écrit &lt;img src="asset:…"&gt; dans le HTML.</p>
              ) : (
                <ul className="space-y-1">
                  {assets.map((asset) => (
                    <li key={asset.key} className="flex items-center gap-1 text-[11px] text-slate-500">
                      <span className="min-w-0 flex-1 truncate" title={asset.filename}>{asset.filename}</span>
                      <button type="button" onClick={() => copy(asset.ref)} className="inline-flex items-center gap-0.5 text-indigo-600">
                        <Copy size={11} /> {copied === asset.ref ? "copié" : "réf."}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {focus.revisions?.length > 1 && (
            <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <h3 className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                <History size={12} /> Versions
              </h3>
              {focus.revisions.map((revision) => (
                <button key={revision.revision} type="button" onClick={() => load(revision.revision)}
                        className={`block w-full truncate text-left text-[11px] hover:text-indigo-600 ${revision.revision === (focus.viewing_revision || focus.revision) ? "font-semibold text-indigo-700" : "text-slate-500"}`}>
                  v{revision.revision} · {new Date(revision.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  {revision.source === "agent" ? " · IA" : ""}{revision.summary ? ` · ${revision.summary}` : ""}
                </button>
              ))}
            </div>
          )}

          {mine && (
            <button type="button" onClick={destroy} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-red-600">
              <Trash2 size={12} /> Supprimer ce Focus
            </button>
          )}
        </aside>

        <main className="min-w-0 space-y-3">
          {savedSlides.length > 0 ? (
            <FocusStage slides={savedSlides} index={previewIndex} onIndexChange={setIndex} keyboard
                        notice={unsavedHere ? <span className="text-xs font-semibold text-amber-600">Aperçu de la version enregistrée</span> : null} />
          ) : (
            <div className="flex aspect-video items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white text-sm text-slate-400">
              Ajoute une diapo ou importe une page HTML.
            </div>
          )}

          {mine && !viewingOld && current && (
            <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <input value={current.title || ""} onChange={(e) => editSlide({ title: e.target.value })} maxLength={200}
                       placeholder="Titre de la diapo"
                       className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
                <button type="button" className={button} onClick={() => replaceRef.current?.click()}>
                  <FileUp size={13} /> Remplacer par un fichier
                </button>
                <input ref={replaceRef} type="file" accept=".html,.htm,text/html" hidden onChange={(e) => importPages(e, true)} />
              </div>
              {big ? (
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Page entière de {sizeLabel(current.html.length)} (trop lourde pour l'éditer ici) : modifie le fichier
                  puis « Remplacer par un fichier », ou demande la modification à l'IA.
                </p>
              ) : (
                <textarea value={current.html || ""} onChange={(e) => editSlide({ html: e.target.value })} spellCheck={false}
                          rows={14}
                          className="w-full rounded-lg border border-slate-200 bg-slate-950 px-3 py-2 font-mono text-xs leading-relaxed text-slate-100 outline-none focus:ring-2 focus:ring-indigo-200" />
              )}
              <textarea value={current.notes || ""} onChange={(e) => editSlide({ notes: e.target.value })} rows={2}
                        maxLength={4000} placeholder="Notes pour toi (non affichées)"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100" />
              <p className="text-[11px] text-slate-400">
                HTML libre, affiché à 1280 × 720 dans un cadre isolé. Scripts depuis cdn.jsdelivr.net, unpkg.com,
                cdnjs, esm.sh (three.js…) ; polices Google Fonts. Enregistre (Ctrl+S) pour voir le rendu.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default FocusTechEditor;
